import { reportTelemetryOnce } from "@coworker/common/helpers/sentry.helpers";
import { useEffect, useMemo } from "react";

type Rotation = { rotation: number };

export type NewFile = { type: "new"; file: File } & Rotation;

export type ExistingFile = {
  type: "existing";
  file: {
    id: string;
    url: string;
    name: string;
    size: number;
  };
} & Rotation;

export type NewOrExistingFile = NewFile | ExistingFile;

function isNewFile(file: NewOrExistingFile): file is NewFile {
  return file.type === "new";
}

export function fileToNewFile(file: File): NewFile {
  return { type: "new", rotation: 0, file };
}

export function filesToNewFiles(files: File[]): NewFile[] {
  return files.map(fileToNewFile);
}

/**
 * Note that existing files in the given array will be filtered out.
 */
export function newOrExistingFilesToFiles(
  newOrExistingFiles: NewOrExistingFile[]
): File[] {
  return newOrExistingFiles.filter(isNewFile).map(({ file }) => file);
}

export function createNewOrExistingFileKey(
  newOrExistingFileKey: NewOrExistingFile
) {
  switch (newOrExistingFileKey.type) {
    case "new": {
      return newOrExistingFileKey.file.name + newOrExistingFileKey.file.size;
    }
    case "existing": {
      return newOrExistingFileKey.file.id;
    }
  }
}

/**
 * Get urls for the given new and existing files.
 * Takes care of revoking the object urls for the new files.
 */
export function useNewOrExistingFileUrls(
  newOrExistingFiles: NewOrExistingFile[]
) {
  const { urls, createdObjectUrls } = useMemo(() => {
    const createdObjectUrls: string[] = [];
    const urls = newOrExistingFiles.map((newOrExistingFile) => {
      switch (newOrExistingFile.type) {
        case "new": {
          try {
            const objectUrl = URL.createObjectURL(newOrExistingFile.file);
            createdObjectUrls.push(objectUrl);
            return objectUrl;
          } catch (error) {
            reportTelemetryOnce(
              `new-file-hook:object-url-create-failed:${newOrExistingFile.file.type || "unknown"}`,
              "Failed to create object URL during image creation in useNewOrExistingFileUrls",
              {
                fileName: newOrExistingFile.file.name,
                fileSize: newOrExistingFile.file.size,
                fileType: newOrExistingFile.file.type,
                error: String(error),
              }
            );
            return "";
          }
        }
        case "existing": {
          return newOrExistingFile.file.url;
        }
      }
    });

    return { urls, createdObjectUrls };
  }, [newOrExistingFiles]);

  useEffect(() => {
    return () => {
      createdObjectUrls.forEach(URL.revokeObjectURL);
    };
  }, [createdObjectUrls]);

  return urls;
}

/**
 * Get url for the given new and existing file.
 * Takes care of revoking the object url if it is a new file.
 */
export function useNewOrExistingFileUrl(newOrExistingFile: NewOrExistingFile) {
  const newOrExistingFiles = useMemo(() => {
    return [newOrExistingFile];
  }, [newOrExistingFile]);

  return useNewOrExistingFileUrls(newOrExistingFiles)[0]!;
}
